import React from 'react'
import { ContextNotification } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'

import { Contract, ContractTenant } from '../../../../../common/validations'
import {
  ContractChangesSection,
  TerminationDisallowedReason,
} from '../../../../../common/types'
import { linksToOtherSection } from './linksToOtherSection'
import { resolvePendingContractChangesNotice } from './resolvePendingContractChangesNotice'

import { useTerminationDisallowedReason } from 'views/ContractChanges/hooks/useTerminationDisallowedReason'
import { StyledLink } from 'components/StyledLink/StyledLink'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

type ContractChangesBannerProps = {
  contract: Contract
  tenants: ContractTenant[]
  otherSection: ContractChangesSection
}

export const ContractChangesBanner: React.FC<ContractChangesBannerProps> = ({
  contract,
  tenants,
  otherSection,
}) => {
  const { t } = useTranslation(['contractModification', 'terminateContract'])
  const reason = contract.terminationDisallowedReason
  const { terminationDisallowedReason } = useTerminationDisallowedReason(
    reason,
    contract.contractType
  )
  const { residentChangesPath, terminateContractPath } = useContractPaths()
  const showOtherSectionLink = linksToOtherSection(contract, otherSection)
  const otherSectionLink =
    otherSection === ContractChangesSection.RESIDENT_CHANGES
      ? {
          path: residentChangesPath,
          label: t('terminateContract:links.seeResidentChanges'),
        }
      : {
          path: terminateContractPath,
          label: t('contractModification:links.seeTerminateContract'),
        }

  const pendingContractChangesNotice = resolvePendingContractChangesNotice(
    contract,
    tenants,
    otherSection
  )

  if (pendingContractChangesNotice) {
    return (
      <ContextNotification type="warning" textAlign="center" className="mb-6">
        {pendingContractChangesNotice}
      </ContextNotification>
    )
  }

  const text =
    reason === TerminationDisallowedReason.NOT_CONTRACT_PARTY &&
    showOtherSectionLink
      ? t(
          'contractModification:terminationDisallowedReasons.not_contract_party'
        )
      : terminationDisallowedReason

  if (!text) {
    return null
  }

  return (
    <ContextNotification textAlign="center" className="mb-6">
      <div>{text}</div>
      {showOtherSectionLink && (
        <StyledLink to={otherSectionLink.path} className="mt-2 inline-block">
          {otherSectionLink.label}
        </StyledLink>
      )}
    </ContextNotification>
  )
}
